/**
 * Shared date and time formatting utilities.
 *
 * Centralizes date/time formatting logic used across the application
 * to ensure consistent formatting and reduce code duplication.
 *
 * All formatters use the browser's local timezone via the Intl API.
 * ISO-8601 / UTC strings from the API are automatically converted to
 * the user's timezone by `new Date(isoString)` + `toLocaleString`.
 *
 * IMPORTANT: Date-only strings like "2026-03-17" are parsed by JS as
 * UTC midnight. In US timezones (behind UTC) this displays as the
 * PREVIOUS day. All helpers use {@link parseDate} to handle this.
 */

/** Shared Intl locale used by all formatters. */
const LOCALE = 'en-US' as const;

/**
 * Parse a date string safely, handling date-only strings ("YYYY-MM-DD")
 * by treating them as local midnight instead of UTC midnight.
 *
 * Without this, `new Date("2026-03-17")` is UTC midnight, which
 * displays as March 16 in US Eastern time (UTC-4).
 */
function parseDate(dateStr: string): Date {
  // Date-only format: exactly "YYYY-MM-DD" (10 chars, no T)
  if (dateStr.length === 10 && !dateStr.includes('T')) {
    return new Date(dateStr + 'T00:00:00');
  }
  return new Date(dateStr);
}

/**
 * Format a date string as a short date (e.g., "Jan 15, 2024").
 * Displays in the browser's local timezone.
 *
 * @param dateStr - ISO date string or null
 * @returns Formatted date string or fallback text
 */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never';
  const date = parseDate(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString(LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date string as a short month/day (e.g., "Jan 15") with no year.
 * Useful for compact UI elements (cards, chips, lists) where the year
 * is implied by context.
 *
 * @param dateStr - ISO date string or null
 * @returns Formatted "Mon DD" string or fallback text
 */
export function formatShortDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never';
  const date = parseDate(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString(LOCALE, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a date with weekday prefix (e.g., "Mon, Jan 15, 2024").
 * Useful for headers/details where the day-of-week adds clarity.
 *
 * @param dateStr - ISO date string or null
 * @returns Formatted "Day, Mon DD, YYYY" string or fallback text
 */
export function formatDateWithWeekday(dateStr: string | null | undefined): string {
  if (!dateStr) return 'No date';
  const date = parseDate(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString(LOCALE, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date string with time (e.g., "Jan 15, 2024, 3:45 PM").
 * Displays in the browser's local timezone.
 *
 * @param dateStr - ISO date string or null
 * @returns Formatted date-time string or fallback text
 */
export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const date = parseDate(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleString(LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Format a date as relative time (e.g., "5m ago", "3h ago", "2d ago").
 * Falls back to a full locale date for items older than 7 days.
 *
 * @param dateStr - ISO date string or null
 * @param now - Reference "now" date (defaults to `new Date()`). Useful for tests.
 * @returns Relative time string like "just now", "5m ago", or full date
 */
export function formatRelativeTime(dateStr: string | null, now?: Date): string {
  if (!dateStr) return 'Never';
  const date = parseDate(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const reference = now ?? new Date();
  const diffMs = reference.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date as a smart "time ago" string suitable for email lists.
 *
 * - Same day  → local time like "3:45 PM"
 * - Yesterday → "Yesterday"
 * - 2–6 days  → "3d ago"
 * - 7+ days   → short date like "Jan 15, 2024"
 *
 * All times are displayed in the browser's local timezone.
 *
 * @param dateStr - ISO date string
 * @param now - Reference "now" date (defaults to `new Date()`). Useful for tests.
 * @returns Formatted time-ago string
 */
export function formatTimeAgo(dateStr: string, now?: Date): string {
  const date = parseDate(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const reference = now ?? new Date();
  const diffMs = reference.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays === 0) {
    return date.toLocaleTimeString(LOCALE, {
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format duration between two dates.
 *
 * @param start - Start date string
 * @param end - End date string or null (if null, shows "Running...")
 * @returns Formatted duration like "5s", "2m 30s", or "Running..."
 */
export function formatDuration(start: string, end: string | null): string {
  if (!end) return 'Running...';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Format file size in bytes to human-readable format.
 *
 * @param bytes - File size in bytes
 * @returns Formatted size like "1.5 KB", "2.3 MB", "1.2 GB", etc.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
